import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import useIsMobile from '../../lib/useIsMobile';
import useSEO from '../../lib/useSEO';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  green: '#16A34A',
  muted: '#64748B',
  light: '#94A3B8',
  border: '#E2E8F0',
  warmBg: '#F8FAFC',
  ctaBg: '#1B2B4B',
  ctaText: '#FFFFFF',
};

const maxWidth = { maxWidth: 1080, margin: '0 auto' };
const font = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function HomePage() {
  const [fee, setFee] = useState('799');
  const isMobile = useIsMobile();
  useSEO({ title: 'Cost Segregation Studies for $799 | ClearSeg', description: 'Your rental property is sitting on $30,000 in tax savings. Cost segregation studies for $799. PE-certified. Delivered in 2 weeks.', canonical: 'https://clearseg.com/' });

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'ClearSeg',
      url: 'https://clearseg.com',
      description: 'PE-certified cost segregation studies for single-family rental properties.',
      address: { '@type': 'PostalAddress', streetAddress: '1970 Swarthmore Ave, Ste 6B', addressLocality: 'Lakewood', addressRegion: 'NJ', postalCode: '08701', addressCountry: 'US' },
      telephone: '+16465331623',
      email: 'info@clearseg.com',
    };
    let script = document.getElementById('org-schema');
    if (!script) { script = document.createElement('script'); script.id = 'org-schema'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(schema);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  useEffect(() => {
    api.get('/config/fees').then((res) => {
      const val = res.data?.single_family;
      if (val) setFee(Number(val).toLocaleString());
    }).catch(() => {});
  }, []);

  const sectionPad = { padding: isMobile ? '48px 20px' : '80px 24px' };

  return (
    <div style={{ background: C.bg, color: C.navy, minHeight: '100vh', fontFamily: font }}>
      <MarketingNav />

      {/* Hero */}
      <section style={{
        ...sectionPad,
        paddingTop: isMobile ? 60 : 100,
        paddingBottom: isMobile ? 48 : 80,
        background: C.white,
        textAlign: 'center',
      }}>
        <div style={{ ...maxWidth, maxWidth: 720 }}>
          <h1 style={{
            fontSize: isMobile ? 30 : 52,
            fontWeight: 800,
            lineHeight: 1.12,
            margin: 0,
            marginBottom: isMobile ? 16 : 20,
            letterSpacing: '-0.03em',
            color: C.navy,
          }}>
            Get Your $3,000 Cost Seg Study for ${fee}
          </h1>
          <p style={{
            fontSize: isMobile ? 16 : 20,
            lineHeight: 1.6,
            color: C.muted,
            margin: 0,
            marginBottom: isMobile ? 24 : 36,
          }}>
            The average ClearSeg client saves <strong style={{ color: C.navy }}>$37,400</strong> in Year 1. PE-certified, delivered to your CPA in 2 weeks — all for just <strong style={{ color: C.navy }}>${fee}</strong>.
          </p>
          <Link
            to="/intake"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: isMobile ? '15px 28px' : '17px 36px',
              background: C.ctaBg,
              color: C.ctaText,
              fontSize: isMobile ? 16 : 18,
              fontWeight: 700,
              borderRadius: 8,
              textDecoration: 'none',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Start Your Study — ${fee}
          </Link>
          <div style={{ marginTop: 16, fontSize: 13, color: C.light }}>
            Takes 2 minutes. Delivered in 2 weeks.
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        background: C.white,
        padding: isMobile ? '16px 20px' : '20px 24px',
      }}>
        <div style={{
          ...maxWidth,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0,
        }}>
          {[
            'PE-Certified Engineers',
            'IRS Audit-Ready Reports',
            'Full Audit Support',
            '2-Week Delivery',
            'Flat $' + fee + ' Fee',
          ].map((item, i, arr) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: isMobile ? '8px 0' : '8px 24px',
              borderRight: !isMobile && i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
              borderBottom: isMobile && i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'center' : 'flex-start',
            }}>
              <span style={{ color: C.green, fontSize: 15, fontWeight: 700 }}>&#10003;</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.muted }}>{item}</span>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: C.muted }}>
          Questions? Call <a href="tel:+16465331623" style={{ color: C.navy, fontWeight: 600, textDecoration: 'none' }}>(646) 533-1623</a>
        </div>
      </section>

      {/* Real Results */}
      <section style={{ ...sectionPad, background: C.bg }}>
        <div style={maxWidth}>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 40 }}>
            <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: 8 }}>
              Real Results From Real Clients
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0 }}>
              Here's what property owners saved with ClearSeg studies this year.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 16 : 20,
            maxWidth: 900,
            margin: '0 auto',
          }}>
            {[
              { name: 'David M.', loc: 'Edison, NJ', prop: '$410K single-family', saved: '$37,312', detail: 'Purchased in 2023. Study identified $106K in reclassifiable assets. Filed with 2024 return.' },
              { name: 'Aaron K.', loc: 'Boca Raton, FL', prop: '$355K single-family', saved: '$32,140', detail: 'First-time investor. CPA had never recommended cost seg. Wished he\'d done it year one.' },
              { name: 'Rachel & Eli S.', loc: 'Scottsdale, AZ', prop: '$520K single-family', saved: '$48,750', detail: 'Portfolio owners with 4 rentals. Started with one study, came back for the rest.' },
            ].map((r, i) => (
              <div key={i} style={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: isMobile ? '24px 20px' : '28px 24px',
              }}>
                <div style={{ fontSize: isMobile ? 30 : 34, fontWeight: 800, color: C.green, marginBottom: 4, letterSpacing: '-0.02em' }}>
                  {r.saved}
                </div>
                <div style={{ fontSize: 13, color: C.light, marginBottom: 16 }}>in first-year tax savings</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>{r.name}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>{r.loc} &middot; {r.prop}</div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                  "{r.detail}"
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgency */}
      <section style={{ background: C.navy, padding: isMobile ? '24px 20px' : '28px 24px', textAlign: 'center' }}>
        <p style={{ ...maxWidth, fontSize: isMobile ? 15 : 17, color: '#FFFFFF', margin: 0, fontWeight: 600 }}>
          Already filed? A look-back study lets you claim missed depreciation without amending your return.
        </p>
      </section>

      {/* What is Cost Seg */}
      <section style={{ ...sectionPad, background: C.white }}>
        <div style={{ ...maxWidth, textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: 8 }}>
            What Is Cost Segregation?
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0, marginBottom: isMobile ? 32 : 48 }}>
            A tax strategy that accelerates depreciation on your rental property — putting money in your pocket now, not in 27 years.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 16 : 24,
            textAlign: 'left',
          }}>
            {[
              { num: '1', title: 'Identify', desc: 'We analyze every component — electrical, plumbing, flooring, cabinetry, fixtures, landscaping — to find assets eligible for accelerated depreciation.' },
              { num: '2', title: 'Reclassify', desc: 'Those components move from the 27.5-year schedule to 5, 7, or 15-year recovery periods per IRS guidelines. This is where the savings come from.' },
              { num: '3', title: 'Save', desc: 'You claim the accelerated depreciation on your income tax return. The savings hit your bottom line this year — not decades from now.' },
            ].map((card) => (
              <div key={card.num} style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: isMobile ? '28px 20px' : '32px 28px',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: C.navy, color: C.ctaText,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 800, marginBottom: 20,
                }}>{card.num}</div>
                <h3 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 700, color: C.navy, margin: 0, marginBottom: 10 }}>{card.title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: C.muted, margin: 0 }}>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ ...sectionPad, background: C.bg }}>
        <div style={{ ...maxWidth, textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: 8 }}>
            How It Works
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0, marginBottom: isMobile ? 32 : 48 }}>
            From start to certified study in 2 weeks. Four steps.
          </p>
          <div style={{ maxWidth: 580, margin: '0 auto', textAlign: 'left' }}>
            {[
              { num: 1, title: 'Submit Your Property Details', desc: 'Fill out our intake form with your property info. Takes 2 minutes.' },
              { num: 2, title: 'We Engineer Your Study', desc: 'Our engineers analyze every building component and categorize assets into proper recovery periods.' },
              { num: 3, title: 'Licensed PE Certification', desc: 'A licensed Professional Engineer certifies every classification. This is what makes your study IRS audit-ready.' },
              { num: 4, title: 'Delivered to Your CPA', desc: 'You get the certified study and asset schedule, ready to apply to your tax return. We can send it directly to your CPA.' },
            ].map((step, i) => (
              <div key={step.num} style={{ display: 'flex', gap: isMobile ? 16 : 24, position: 'relative' }}>
                {i < 3 && (
                  <div style={{
                    position: 'absolute',
                    left: isMobile ? 17 : 19,
                    top: isMobile ? 40 : 44,
                    bottom: -4,
                    width: 2,
                    background: C.border,
                  }} />
                )}
                <div style={{
                  flexShrink: 0,
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  borderRadius: '50%',
                  background: C.navy,
                  color: C.ctaText,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isMobile ? 14 : 16, fontWeight: 800,
                  position: 'relative', zIndex: 1,
                }}>{step.num}</div>
                <div style={{ paddingBottom: i < 3 ? (isMobile ? 28 : 40) : 0, paddingTop: 2 }}>
                  <h3 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: C.navy, margin: 0, marginBottom: 6 }}>{step.title}</h3>
                  <p style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.65, color: C.muted, margin: 0 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ ...sectionPad, background: C.white }}>
        <div style={{ ...maxWidth, textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: 8 }}>
            One Price. Everything Included.
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0, marginBottom: isMobile ? 28 : 40 }}>
            No hidden fees. No upsells. No surprises.
          </p>
          <div style={{
            maxWidth: 440,
            margin: '0 auto',
            background: C.bg,
            border: `2px solid ${C.navy}`,
            borderRadius: 12,
            padding: isMobile ? '32px 24px' : '44px 36px',
          }}>
            <div style={{
              fontSize: isMobile ? 48 : 60,
              fontWeight: 800,
              color: C.navy,
              lineHeight: 1,
              marginBottom: 4,
              letterSpacing: '-0.03em',
            }}>${fee}</div>
            <div style={{ fontSize: 15, color: C.muted, marginBottom: isMobile ? 24 : 32 }}>per single-family property</div>
            <div style={{ textAlign: 'left', marginBottom: isMobile ? 24 : 32 }}>
              {[
                'Full component-level analysis',
                'PE review & certification',
                'IRS-compliant, audit-ready report',
                'Delivered directly to your CPA',
                'Bonus depreciation analysis included',
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 0',
                  borderBottom: i < 4 ? `1px solid ${C.border}` : 'none',
                }}>
                  <span style={{ color: C.green, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>&#10003;</span>
                  <span style={{ fontSize: 15, color: C.navy }}>{item}</span>
                </div>
              ))}
            </div>
            <Link
              to="/intake"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: isMobile ? '15px 24px' : '17px 32px',
                background: C.ctaBg,
                color: C.ctaText,
                fontSize: isMobile ? 16 : 17,
                fontWeight: 700,
                borderRadius: 8,
                textDecoration: 'none',
                transition: 'opacity 0.15s',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Start Your Study
            </Link>
          </div>
        </div>
      </section>

      {/* Who We've Worked With */}
      <section style={{ ...sectionPad, background: C.bg }}>
        <div style={{ ...maxWidth, textAlign: 'center' }}>
          <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: 8 }}>
            Trusted by Property Owners Nationwide
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 17, color: C.muted, margin: 0, marginBottom: isMobile ? 28 : 40 }}>
            Our clients include individual landlords, small portfolio owners, and real estate professionals across 38 states.
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? 16 : 20,
            maxWidth: 700,
            margin: '0 auto',
          }}>
            {[
              { stat: '1,200+', label: 'Studies Delivered' },
              { stat: '38', label: 'States Served' },
              { stat: '$28K', label: 'Avg. Year-1 Savings' },
              { stat: '35x', label: 'Avg. Return on Fee' },
            ].map((s, i) => (
              <div key={i} style={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: isMobile ? '20px 12px' : '24px 16px',
              }}>
                <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: C.navy, letterSpacing: '-0.02em' }}>{s.stat}</div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section style={{ ...sectionPad, background: C.white }}>
        <div style={{ ...maxWidth, maxWidth: 680 }}>
          <h2 style={{ fontSize: isMobile ? 24 : 34, fontWeight: 800, color: C.navy, margin: 0, marginBottom: isMobile ? 24 : 36, textAlign: 'center' }}>
            Common Questions
          </h2>
          {[
            { q: 'Is this legitimate? Will the IRS accept it?', a: 'Yes. Cost segregation is a well-established tax strategy backed by IRS guidelines. Every ClearSeg study is certified by a licensed Professional Engineer — the same standard used by Fortune 500 companies.' },
            { q: 'Do I need to have purchased my property recently?', a: 'No. You can do a cost segregation study on a property you\'ve owned for years. A "look-back" study lets you claim missed depreciation without amending prior returns.' },
            { q: 'What if I only own one rental property?', a: 'That\'s exactly who we built this for. Most cost seg firms focus on commercial properties and charge $5,000–$15,000. We specialize in single-family rentals at a price that makes sense.' },
            { q: 'What does my CPA need to do?', a: 'Very little. We deliver a completed study with an asset schedule your CPA can apply directly to your return. We can also send it to them on your behalf.' },
          ].map((faq, i) => (
            <div key={i} style={{
              borderBottom: i < 3 ? `1px solid ${C.border}` : 'none',
              padding: '20px 0',
            }}>
              <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: C.navy, marginBottom: 8 }}>{faq.q}</div>
              <div style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.65, color: C.muted }}>{faq.a}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Link to="/faq" style={{ fontSize: 14, color: C.accent, fontWeight: 600, textDecoration: 'none' }}>
              View all FAQs &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{
        padding: isMobile ? '56px 20px' : '80px 24px',
        background: C.navy,
        textAlign: 'center',
      }}>
        <div style={{ ...maxWidth, maxWidth: 600 }}>
          <h2 style={{
            fontSize: isMobile ? 26 : 38,
            fontWeight: 800,
            color: '#FFFFFF',
            margin: 0,
            marginBottom: 12,
            lineHeight: 1.2,
          }}>
            ${fee}. 2 Weeks. Thousands Back.
          </h2>
          <p style={{
            fontSize: isMobile ? 15 : 18,
            color: '#94A3B8',
            margin: 0,
            marginBottom: isMobile ? 28 : 36,
            lineHeight: 1.6,
          }}>
            Stop leaving money on the table. Every tax year without a cost seg study is money you're not getting back.
          </p>
          <Link
            to="/intake"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: isMobile ? '15px 32px' : '17px 40px',
              background: '#FFFFFF',
              color: C.navy,
              fontSize: isMobile ? 16 : 18,
              fontWeight: 700,
              borderRadius: 8,
              textDecoration: 'none',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.92'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            Start Your Study
          </Link>
          <div style={{ marginTop: 16, fontSize: 13, color: '#64748B' }}>
            PE-certified &middot; IRS-compliant &middot; Delivered to your CPA
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage;
